import { memo, useMemo } from 'react';
import type { computePitchControl } from '@4cm/core/tactics/overlays';

/**
 * §17 — Pitch Control overlay for the 2D tactics board.
 *
 * Pitch Control is a continuous probability surface — for every point
 * on the pitch, what's the home team's expected ownership share if a
 * ball arrived there? A weighted upgrade over Voronoi (which is just
 * nearest-neighbour). Reference: Spearman 2018, "Beyond Expected
 * Goals", plus modern broadcast tactical analysis (Sky MNF, NBC PL
 * pitch-ownership maps).
 *
 * The 2D pitch SVG (`PitchSvg`) uses the landscape mapping
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * so each grid cell is emitted as an axis-aligned `<rect>` in the
 * landscape orientation: a cell at world (cx, cz) of size `step`
 * becomes an SVG rect of size step × step at SVG (-cz - step/2, cx -
 * step/2).
 *
 * The opacity prop bakes directly into the rgba fill — same pattern as
 * VoronoiLayer2D — so coaches can dial the surface up or down without
 * fighting an extra `opacity` attribute on every rect.
 *
 * Render-side optimisation: cells whose home share sits in a narrow
 * "neutral" band around 0.5 are skipped — they'd paint as middling
 * grey which adds no information versus the underlying pitch texture.
 * Threshold is intentionally tight (±0.04) so the contested zone is
 * still visible at the midline.
 */
export type PitchControlLayer2DGrid = ReturnType<typeof computePitchControl>;

export interface PitchControlLayer2DProps {
  grid: PitchControlLayer2DGrid;
  /** Caller-controlled opacity in [0, 1]. */
  opacity: number;
}

/**
 * Half-width of the "skip this cell" band around homeShare=0.5. Cells
 * that fall inside this band don't get painted — they'd be mid-grey
 * which conveys no information. Tight band so the contested midline
 * region itself still shows up.
 */
const NEUTRAL_SKIP_BAND = 0.04;

// Home green (`#4ade80` = rgb(74, 222, 128)).
// Away amber (`#fbbf24` = rgb(251, 191, 36)).
// Neutral grey midpoint at homeShare=0.5.
const HOME_RGB = { r: 74, g: 222, b: 128 } as const;
const AWAY_RGB = { r: 251, g: 191, b: 36 } as const;
const NEUTRAL_RGB = { r: 160, g: 160, b: 160 } as const;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Interpolate between home green (share=1), neutral grey (share=0.5)
 * and away amber (share=0). Two linear segments meeting at 0.5 — a
 * single linear lerp between home and away would muddy the midline
 * into a brownish tone, where a neutral grey reads clearly as
 * "contested".
 */
function fillFor(homeShare: number, opacity: number): string {
  let r: number;
  let g: number;
  let b: number;
  if (homeShare >= 0.5) {
    // Home half: lerp neutral → home as share goes 0.5 → 1.
    const t = (homeShare - 0.5) * 2;
    r = lerp(NEUTRAL_RGB.r, HOME_RGB.r, t);
    g = lerp(NEUTRAL_RGB.g, HOME_RGB.g, t);
    b = lerp(NEUTRAL_RGB.b, HOME_RGB.b, t);
  } else {
    // Away half: lerp neutral → away as share goes 0.5 → 0.
    const t = (0.5 - homeShare) * 2;
    r = lerp(NEUTRAL_RGB.r, AWAY_RGB.r, t);
    g = lerp(NEUTRAL_RGB.g, AWAY_RGB.g, t);
    b = lerp(NEUTRAL_RGB.b, AWAY_RGB.b, t);
  }
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${opacity})`;
}

interface RectSpec {
  key: string;
  x: number;
  y: number;
  size: number;
  fill: string;
  share: number;
}

function PitchControlLayer2DInner({ grid, opacity }: PitchControlLayer2DProps) {
  // Memoise the rect list so re-renders driven by sibling state (HUD
  // toggles etc.) skip the per-cell loop. Cells are skipped near
  // homeShare=0.5 to keep the paint cost down — see file header.
  const rects = useMemo<RectSpec[]>(() => {
    if (!grid || grid.homeShare.length === 0) return [];
    const { nRows, nCols, step, halfL, halfW, homeShare } = grid;
    const out: RectSpec[] = [];
    for (let r = 0; r < nRows; r++) {
      // World x at the centre of this row.
      const cx = -halfW + (r + 0.5) * step;
      for (let c = 0; c < nCols; c++) {
        const share = homeShare[r * nCols + c]!;
        // Skip the near-neutral band — these cells carry no signal.
        // Also skip exact zeros (empty grid / no contributors): a grid
        // with no players should paint nothing.
        if (share === 0) continue;
        if (Math.abs(share - 0.5) < NEUTRAL_SKIP_BAND) continue;
        // World z at the centre of this col.
        const cz = -halfL + (c + 0.5) * step;
        // Landscape mapping: world (x, z) → SVG (-z, x). A cell
        // centred at (cx, cz) of size step×step has SVG top-left at
        // (-cz - step/2, cx - step/2).
        out.push({
          key: `${r}-${c}`,
          x: -cz - step / 2,
          y: cx - step / 2,
          size: step,
          fill: fillFor(share, opacity),
          share,
        });
      }
    }
    return out;
  }, [grid, opacity]);

  if (rects.length === 0) return null;

  return (
    <g
      className="t2d-pitchcontrol-layer"
      data-testid="t2d-pitchcontrol-layer"
      pointerEvents="none"
      // Clipped to the pitch rectangle — see VoronoiLayer2D + PitchSvg.
      clipPath="url(#t2d-pitch-clip)"
    >
      {rects.map((r) => (
        <rect
          key={r.key}
          x={r.x}
          y={r.y}
          width={r.size}
          height={r.size}
          fill={r.fill}
          data-testid={`t2d-pitchcontrol-cell-${r.key}`}
          data-share={r.share.toFixed(3)}
        />
      ))}
    </g>
  );
}

/** Memoised — grid is recomputed only when token positions change so
 *  the parent typically hands the same reference across unrelated
 *  renders (HUD chip toggles etc.). */
export const PitchControlLayer2D = memo(PitchControlLayer2DInner);
